import { Col, Icon, Row, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import React, { useEffect, useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import styled from 'styled-components';

const ListContainer = styled.div`
  width: 100%;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  border-radius: 8px;
  background: white;
`;

const PlayerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  background: ${(props) => (props.selected ? `${theme.SECONDARY}10` : 'white')};
  border-bottom: 1px solid ${theme.GREY_20};
  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${(props) =>
      props.disabled ? 'white' : props.selected ? `${theme.SECONDARY}15` : theme.GREY_10};
  }
`;

const CheckBox = styled.div`
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border-radius: 5px;
  border: 2px solid ${(props) => (props.selected ? theme.SECONDARY : theme.GREY_30)};
  background: ${(props) => (props.selected ? theme.SECONDARY : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PhotoCircle = styled.div`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 100px;
  background-color: ${(props) => (props.src ? '#fff' : theme.GREY_20)};
  background-image: url('${(props) => props.src}');
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.GREY_20};
`;

const NumberBadge = styled.div`
  min-width: 26px;
  height: 26px;
  padding: 0 6px;
  border-radius: 6px;
  background: ${theme.GREY_10};
  color: ${theme.GREY_70};
  font-family: CeraPro;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid ${theme.GREY_30};
  border-radius: 8px;
  margin-bottom: 10px;
  input {
    flex: 1;
    border: none;
    outline: none;
    font-family: CeraPro;
    font-size: 14px;
    color: ${theme.GREY_80};
    background: transparent;
  }
`;

const initials = (name = '') =>
  name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

// Player checkbox picker for game-day poll options. Fetches the channel's selectable
// players from /admin/polls/players (the confirmed lineup once published ~20-40 min before
// kickoff, otherwise the full synced squad) and lets the admin tick players. Each ticked
// player becomes a poll option { playerId } — name + photo are derived server-side.
//
// `value` is the poll's WHOLE option list, so a game-day poll can mix player picks with
// custom free-text answers: entries without a `playerId` are passed through untouched and
// only count against `maxSelected`, which caps the combined list.
const PollPlayerPicker = ({ teamId, gameId, value = [], onChange, maxSelected, hasError }) => {
  const [players, setPlayers] = useState([]);
  const [source, setSource] = useState(null); // 'lineup' | 'squad'
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!teamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const qs = `teamId=${teamId}${gameId ? `&gameId=${gameId}` : ''}`;
      const res = await Api.get(`/admin/polls/players?${qs}`, null, { silent: true });
      if (cancelled) return;
      setPlayers(res?.data?.players || []);
      setSource(res?.data?.source || null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, gameId]);

  const selectedIds = useMemo(
    () => new Set(value.map((v) => v.playerId).filter(Boolean)),
    [value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [players, query]);

  // The cap is on the poll's options, not on players alone — custom free-text answers
  // already in `value` take slots away from the picker.
  const atLimit = !!maxSelected && value.length >= maxSelected;

  const toggle = (p) => {
    if (!p._id) return; // lineup player not synced locally → can't be a poll option
    const id = p._id;
    if (selectedIds.has(id)) {
      onChange(value.filter((v) => v.playerId !== id));
    } else {
      if (atLimit) return; // capped at maxSelected options
      onChange([...value, { playerId: id, text: p.name, photo: p.photo }]);
    }
  };

  if (loading) {
    return (
      <Row center pad="8px 0">
        <Spinner2 />
        <Text marg="0 0 0 8px" col={theme.GREY_60}>
          Loading players...
        </Text>
      </Row>
    );
  }

  if (!players.length) {
    return (
      <Col
        wid="100%"
        pad="12px 14px"
        style={{ borderRadius: 8, border: `1px solid ${theme.GREY_30}`, background: theme.GREY_10 }}
      >
        <Text c2 col={theme.GREY_60}>
          No players found for this channel yet. The squad syncs from the match provider — pick a
          match closer to kickoff for the confirmed lineup.
        </Text>
      </Col>
    );
  }

  return (
    <Col wid="100%">
      <Row between center marg="0 0 8px">
        <Text c2 col={theme.GREY_60}>
          {source === 'lineup'
            ? 'Confirmed lineup (starting XI + subs)'
            : 'Full squad — confirmed lineup appears closer to kickoff'}
        </Text>
        <Text c2 col={atLimit ? theme.ORANGE : theme.SECONDARY}>
          {selectedIds.size} selected
          {maxSelected ? ` · ${value.length} / ${maxSelected} options` : ''}
        </Text>
      </Row>

      <SearchBox>
        <FiSearch size={15} color={theme.GREY_50} />
        <input
          placeholder="Search players..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </SearchBox>

      <ListContainer hasError={hasError}>
        {filtered.map((p, i) => {
          const selected = !!p._id && selectedIds.has(p._id);
          // Block unticked rows once the cap is hit; ticked rows stay clickable to remove.
          const disabled = !p._id || (atLimit && !selected);
          return (
            <PlayerRow
              key={p._id || p.apiPlayerId || i}
              selected={selected}
              disabled={disabled}
              onClick={() => toggle(p)}
              title={
                !p._id
                  ? 'Not available to select yet'
                  : atLimit && !selected
                    ? `A poll can have at most ${maxSelected} options`
                    : undefined
              }
            >
              <CheckBox selected={selected}>
                {selected && <Icon type="check" dimensions={16} col="white" />}
              </CheckBox>
              <PhotoCircle src={p.photo || ''}>
                {!p.photo && (
                  <Text c2 col={theme.GREY_60} style={{ fontWeight: 600 }}>
                    {initials(p.name)}
                  </Text>
                )}
              </PhotoCircle>
              <Col style={{ flex: 1, minWidth: 0 }}>
                <Text col={theme.GREY_80} style={{ fontWeight: 500 }}>
                  {p.name}
                  {p.isStarter && (
                    <span style={{ color: theme.GREEN, fontSize: 11, marginLeft: 8 }}>STARTER</span>
                  )}
                </Text>
                {p.position && (
                  <Text c2 col={theme.GREY_50}>
                    {p.position}
                  </Text>
                )}
              </Col>
              {p.number != null && <NumberBadge>#{p.number}</NumberBadge>}
            </PlayerRow>
          );
        })}
      </ListContainer>
    </Col>
  );
};

export default PollPlayerPicker;
